// src/components/PredictionDashboard/GlobalPredictionChart.tsx
import React from 'react';
import { Box, Typography, CircularProgress, Paper, Divider } from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

// --- MODIFICACIÓN CRÍTICA: EXPORTAR LA INTERFAZ ---
export interface ChartDataPoint {
  prediction_date: string;
  value: number;            // El Plan (Recomendado)
  value_base?: number;      // El Suelo (Inercia)
  value_potential?: number; // El Techo (IA)
}

interface GlobalPredictionChartProps {
  data: ChartDataPoint[];
  isLoading: boolean;
  isError: boolean;
  valueFormatter: (value: number) => string;
  lineColor: string;
  chartTitle: string;
  chartType?: 'area' | 'bar';
}

const CHART_GRID_STYLE = { strokeDasharray: "3 3", stroke: "#334155", opacity: 0.3 };
const CHART_AXIS_STYLE = { stroke: "#94a3b8", fontSize: 11, tickLine: false, axisLine: { stroke: '#334155' } };

// --- TOOLTIP EJECUTIVO (RICO EN DATOS) ---
const CustomTooltip: React.FC<any> = ({ active, payload, label, formatter, title }) => {
    if (active && payload && payload.length) {
        let dateLabel = label;
        try {
            if (label.includes('-')) {
                 dateLabel = format(parseISO(label), 'MMMM yyyy', { locale: es });
            }
        } catch(e) {}

        // Extracción segura de datos
        const point = payload[0].payload as ChartDataPoint;
        const valPlan = point.value;
        const valBase = point.value_base || 0;
        const valPot = point.value_potential || 0;
        
        // Cálculo de impacto (Solo si tenemos base)
        const hasDetails = valBase > 0;
        const delta = valPlan - valBase;
        const isPositive = delta >= 0;

        return (
            <Paper elevation={4} sx={{ p: 2, backgroundColor: 'rgba(15, 23, 42, 0.98)', border: '1px solid #475569', backdropFilter: 'blur(8px)', minWidth: 220 }}>
                {/* Cabecera Fecha */}
                <Typography sx={{ color: '#94a3b8', fontWeight: 'bold', mb: 1, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {dateLabel}
                </Typography>
                
                {/* 1. EL PLAN (Dato Principal) */}
                <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ color: payload[0].stroke || payload[0].fill, fontSize: '1.1rem', fontWeight: 800 }}>
                        {formatter(valPlan)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {title} (Estrategia)
                    </Typography>
                </Box>

                {hasDetails && (
                    <>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />
                        
                        {/* 2. CONTEXTO (Base y Potencial) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* Techo */}
                            {valPot > valPlan && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                                    <Typography variant="caption">Máx. Potencial:</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatter(valPot)}</Typography>
                                </Box>
                            )}
                            
                            {/* Suelo */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#94a3b8' }}>
                                <Typography variant="caption">Inercia Histórica:</Typography>
                                <Typography variant="caption">{formatter(valBase)}</Typography>
                            </Box>
                            
                            {/* Impacto */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, color: isPositive ? '#4ade80' : '#ef4444' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>IMPACTO:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                    {isPositive ? '+' : ''}{formatter(delta)}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </Paper>
        );
    }
    return null;
};

const GlobalPredictionChart: React.FC<GlobalPredictionChartProps> = ({
  data, isLoading, isError, valueFormatter, lineColor, chartTitle, chartType = 'area'
}) => {
  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress sx={{ color: lineColor }} /></Box>;
  if (isError) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="error.main">Error de carga</Typography></Box>;
  if (!data || data.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><Typography color="text.secondary">Sin datos proyectados</Typography></Box>;

  // --- RENDERIZADO DE BARRAS (MODO TÁCTICO) ---
  if (chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
             <CartesianGrid {...CHART_GRID_STYLE} vertical={false} />
             <XAxis
              dataKey="prediction_date"
              tickFormatter={(label) => {
                 if (label.includes('-') && label.length > 4) {
                     try { return format(parseISO(label), 'MMM yy', { locale: es }); } catch { return label; }
                 }
                 return label;
              }}
              interval={0} 
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              tickMargin={10}
              axisLine={{ stroke: '#334155' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(0)}k` : val}
              width={35}
              {...CHART_AXIS_STYLE}
            />
            <Tooltip
              content={<CustomTooltip formatter={valueFormatter} title={chartTitle} />}
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            />
            <Bar 
                dataKey="value" 
                fill={lineColor} 
                radius={[4, 4, 0, 0]} 
                barSize={40} 
                fillOpacity={0.9}
            />
          </BarChart>
        </ResponsiveContainer>
      );
  }

  // --- RENDERIZADO DE ÁREA (MODO ESTRATÉGICO - DEFAULT) ---
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id={`gradient-${lineColor}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={lineColor} stopOpacity={0.3}/>
            <stop offset="95%" stopColor={lineColor} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid {...CHART_GRID_STYLE} vertical={false} />
        <XAxis
          dataKey="prediction_date"
          tickFormatter={(label) => {
             if (label.includes('-') && label.length > 4) {
                 try { return format(parseISO(label), 'MMM yy', { locale: es }); } catch { return label; }
             }
             return label;
          }}
          interval={0} 
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          tickMargin={10}
          axisLine={{ stroke: '#334155' }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(0)}k` : val}
          width={35}
          {...CHART_AXIS_STYLE}
        />
        <Tooltip
          content={<CustomTooltip formatter={valueFormatter} title={chartTitle} />}
          cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={lineColor}
          strokeWidth={3}
          fillOpacity={1}
          fill={`url(#gradient-${lineColor})`}
          activeDot={{ r: 6, strokeWidth: 0, fill: lineColor }}
          connectNulls={true}
          animationDuration={1500}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default GlobalPredictionChart;